import React from 'react';
import {Pressable, View, useWindowDimensions} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {Masterclass} from '../../../Hooks/LmsCourseApi/useMasterclasses';

import CourseVideoPlayer from './CourseVideoPlayer';
import {F} from '../courseTheme';

type Props = {
  item: Masterclass;
  onClose: () => void;
};

/**
 * Watch a recording before building a course from it.
 *
 * Generation takes about an hour and these rows are titled by Zoom, so
 * without this the only way to learn what is on a recording is to spend that
 * hour and read the result. Roughly half of what the pipeline produces is
 * decided before anyone clicks Build — a personal 1-1 session, a class that
 * is mostly screen-share, a recording that starts twenty minutes late — and
 * all of it is visible in fifteen seconds of scrubbing.
 *
 * `segment` is null and `onSegmentEnd` a no-op: the player's windowing exists
 * to keep a student inside one chapter, and there are no chapters yet. This
 * is the whole recording, free to scrub.
 */
export default function MasterclassPreview({item, onClose}: Props) {
  const {width, height} = useWindowDimensions();
  const playerWidth = Math.min(880, width - 48);

  return (
    <View
      // Not a react-native Modal: this screen is react-native-web and the
      // picker already lives inside a positioned container, so an absolute
      // overlay composes predictably where a Modal's own portal does not.
      accessibilityViewIsModal
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width,
        height,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(20,18,17,0.72)',
        padding: 24,
        zIndex: 50,
      }}>
      {/* Clicking away closes, which is what everyone tries first. Behind the
          panel, so a press inside the panel never reaches it. */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Close preview"
        onPress={onClose}
        style={{position: 'absolute', top: 0, left: 0, width, height}}
      />

      <View
        style={{
          width: playerWidth,
          borderRadius: F.radius.card,
          backgroundColor: F.white,
          overflow: 'hidden',
        }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12,
            paddingHorizontal: 16,
            paddingVertical: 12,
          }}>
          <View style={{flex: 1, minWidth: 0}}>
            <CCText
              style={{fontSize: 16, fontWeight: '700', color: F.brownDark}}
              numberOfLines={1}>
              {item.title}
            </CCText>
            <CCText style={{fontSize: 12, fontWeight: '500', color: F.brown}}>
              {[
                item.coach,
                item.class_name || item.category,
                item.duration_min ? `${item.duration_min} min` : '',
              ]
                .filter(Boolean)
                .join(' · ')}
            </CCText>
          </View>

          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close preview"
            onPress={onClose}
            hitSlop={8}
            style={({pressed}) => ({
              width: 44,
              height: 44,
              borderRadius: 22,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: pressed ? 'rgba(38,35,34,0.10)' : 'transparent',
            })}>
            <Ionicons name="close" size={22} color={F.brownDark} />
          </Pressable>
        </View>

        <CourseVideoPlayer
          src={item.video_url}
          segment={null}
          onSegmentEnd={() => {}}
          posterTitle={item.title}
        />
      </View>
    </View>
  );
}
